import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  Calendar,
  Clock,
  MoreHorizontal,
  User,
} from "lucide-react";
import type { ComponentType } from "react";
import clsx from "clsx";
import { useProtectedNavigate } from "@/hooks/useProtectedNavigate";
import MoreSheet from "./MoreSheet";

interface Tab {
  key: string;
  to: string;
  match: string;
  icon: ComponentType<{ className?: string }>;
  requiresAuth?: boolean;
}

const TABS: Tab[] = [
  { key: "sessions", to: "/sessions/my", match: "/sessions", icon: Clock, requiresAuth: true },
  { key: "events", to: "/events", match: "/events", icon: Calendar },
  { key: "dashboard", to: "/dashboard", match: "/dashboard", icon: BarChart3, requiresAuth: true },
  { key: "myPage", to: "/my-page", match: "/my-page", icon: User, requiresAuth: true },
];

export default function BottomTabBar() {
  const { t } = useTranslation("common");
  const location = useLocation();
  const protectedNavigate = useProtectedNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  // Route changes (including More-sheet selections) dismiss the sheet.
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  const handleTab = (tab: Tab) => {
    protectedNavigate(tab.to, tab.requiresAuth);
  };

  const isTabActive = (tab: Tab) =>
    location.pathname === tab.match ||
    location.pathname.startsWith(`${tab.match}/`);

  return (
    <>
      <nav
        aria-label={t("navigation.primaryNav")}
        className="fixed bottom-0 inset-x-0 z-50 bg-white border-t border-secondary-200 lg:hidden pb-[env(safe-area-inset-bottom)]"
      >
        <div className="grid grid-cols-5 h-[var(--tabbar-height)]">
          {TABS.map((tab) => {
            const active = isTabActive(tab);
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTab(tab)}
                aria-current={active ? "page" : undefined}
                className={clsx(
                  "flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500",
                  active ? "text-primary-600" : "text-secondary-500",
                )}
              >
                <tab.icon className="h-5 w-5" aria-hidden="true" />
                {t(`navigation.${tab.key}`)}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-expanded={moreOpen}
            aria-haspopup="dialog"
            className="flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-secondary-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
          >
            <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
            {t("navigation.more")}
          </button>
        </div>
      </nav>
      {moreOpen && <MoreSheet onClose={() => setMoreOpen(false)} />}
    </>
  );
}
